import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    fetchProjects,
    fetchSettings,
    verifyAdmin,
    adminCreateProject,
    adminUpdateProject,
    adminDeleteProject,
    adminListInquiries,
    adminUpdateSettings,
    adminListClips,
    adminCreateClip,
    adminUpdateClip,
    adminDeleteClip,
    adminImportClips,
    adminApproveClip,
    adminArchiveClip,
    adminFeatureClip,
} from "../lib/api";
import { detectSource } from "../lib/embed";

const EMPTY_PROJECT = {
    title: "",
    year: new Date().getFullYear(),
    role: "Director of Photography",
    category: "Narrative",
    description: "",
    thumbnail_url: "",
    embed_url: "",
    gallery_urls: "",
    awards: "",
    featured: false,
    order: 0,
};

const CATEGORIES = [
    "Narrative",
    "Commercial",
    "Short Film",
    "Documentary",
    "Music Video",
    "Other",
];

const CLIP_CATEGORIES = [
    "On Set",
    "Behind The Camera",
    "Commercial",
    "Narrative",
    "Comedy",
    "AI Experiments",
    "Archive",
    "Experimental",
];

const EMPTY_CLIP = {
    title: "",
    year: new Date().getFullYear(),
    role: "",
    category: "Archive",
    caption: "",
    embed_url: "",
    thumbnail_url: "",
    poster_url: "",
    project_name: "",
    source: "upload",
    status: "published",
    featured: false,
    auto_advance_seconds: "",
    show_intro_overlay: false,
    order: 0,
};

const CLIP_STATUS_FILTERS = [
    { id: "all", label: "All" },
    { id: "published", label: "Published" },
    { id: "pending", label: "Pending" },
    { id: "archived", label: "Archived" },
    { id: "featured", label: "Featured" },
];

const STATUS_BADGES = {
    pending: "Pending review",
    published: "Live",
    archived: "Archived",
};

const Admin = () => {
    const [pass, setPass] = useState(localStorage.getItem("admin_pass") || "");
    const [authed, setAuthed] = useState(false);
    const [projects, setProjects] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [settings, setSettings] = useState(null);
    const [editing, setEditing] = useState(null); // project being edited
    const [form, setForm] = useState(EMPTY_PROJECT);
    const [tab, setTab] = useState("projects");

    // Clips state
    const [clips, setClips] = useState([]);
    const [clipEditing, setClipEditing] = useState(null);
    const [clipForm, setClipForm] = useState(EMPTY_CLIP);
    const [clipStatusFilter, setClipStatusFilter] = useState("published");
    const [importUrls, setImportUrls] = useState("");
    const [importing, setImporting] = useState(false);

    const tryAuth = async (p) => {
        try {
            await verifyAdmin(p);
            localStorage.setItem("admin_pass", p);
            setAuthed(true);
            await Promise.all([
                reloadProjects(),
                reloadInquiries(p),
                reloadSettings(),
                reloadClips(),
            ]);
        } catch {
            setAuthed(false);
            toast.error("Invalid passcode");
        }
    };

    const reloadProjects = async () => {
        const list = await fetchProjects();
        setProjects(list);
    };
    const reloadInquiries = async (p) => {
        const list = await adminListInquiries(p || pass);
        setInquiries(list);
    };
    const reloadSettings = async () => {
        const s = await fetchSettings();
        setSettings(s);
    };
    const reloadClips = async (filterArg) => {
        const filter = filterArg ?? clipStatusFilter;
        const params = {};
        if (filter === "featured") {
            params.featured = true;
        } else if (filter && filter !== "all") {
            params.status = filter;
        }
        const list = await adminListClips(pass, params);
        setClips(list);
    };

    useEffect(() => {
        if (pass) tryAuth(pass);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onLogin = (e) => {
        e.preventDefault();
        tryAuth(pass);
    };

    const startNew = () => {
        setEditing(null);
        setForm(EMPTY_PROJECT);
    };
    const startEdit = (p) => {
        setEditing(p.id);
        setForm({
            ...p,
            embed_url: p.embed_url || "",
            gallery_urls: (p.gallery_urls || []).join("\n"),
            awards: (p.awards || []).join("\n"),
        });
    };

    const saveProject = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            year: Number(form.year),
            order: Number(form.order || 0),
            gallery_urls: form.gallery_urls
                ? form.gallery_urls.split("\n").map((s) => s.trim()).filter(Boolean)
                : [],
            awards: form.awards
                ? form.awards.split("\n").map((s) => s.trim()).filter(Boolean)
                : [],
            embed_url: form.embed_url || null,
        };
        try {
            if (editing) {
                await adminUpdateProject(pass, editing, payload);
                toast.success("Project updated");
            } else {
                await adminCreateProject(pass, payload);
                toast.success("Project created");
            }
            await reloadProjects();
            startNew();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const deleteProject = async (id) => {
        if (!window.confirm("Delete this project?")) return;
        try {
            await adminDeleteProject(pass, id);
            toast.success("Project deleted");
            await reloadProjects();
            if (editing === id) startNew();
        } catch {
            toast.error("Delete failed");
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        try {
            await adminUpdateSettings(pass, settings);
            toast.success("Settings saved");
        } catch {
            toast.error("Save failed");
        }
    };

    // -------- Clip handlers -------- //
    const startNewClip = () => {
        setClipEditing(null);
        setClipForm(EMPTY_CLIP);
    };
    const startEditClip = (c) => {
        setClipEditing(c.id);
        setClipForm({
            ...c,
            embed_url: c.embed_url || "",
            thumbnail_url: c.thumbnail_url || "",
            poster_url: c.poster_url || "",
            project_name: c.project_name || "",
            role: c.role || "",
            year: c.year ?? "",
            status: c.status || "published",
            featured: !!c.featured,
            auto_advance_seconds: c.auto_advance_seconds ?? "",
            show_intro_overlay: !!c.show_intro_overlay,
        });
    };
    const saveClip = async (e) => {
        e.preventDefault();
        const rawSecs = clipForm.auto_advance_seconds;
        let autoSecs = null;
        if (rawSecs !== "" && rawSecs !== null && rawSecs !== undefined) {
            const n = Number(rawSecs);
            if (!Number.isFinite(n) || n < 3 || n > 60) {
                toast.error("Auto-advance seconds must be between 3 and 60.");
                return;
            }
            autoSecs = n;
        }
        const payload = {
            ...clipForm,
            year: clipForm.year ? Number(clipForm.year) : null,
            order: Number(clipForm.order || 0),
            embed_url: clipForm.embed_url || null,
            thumbnail_url: clipForm.thumbnail_url || null,
            poster_url: clipForm.poster_url || null,
            project_name: clipForm.project_name || null,
            source:
                clipForm.source === "auto" || !clipForm.source
                    ? detectSource(clipForm.embed_url)
                    : clipForm.source,
            status: clipForm.status || "published",
            featured: !!clipForm.featured,
            auto_advance_seconds: autoSecs,
            show_intro_overlay: !!clipForm.show_intro_overlay,
        };
        try {
            if (clipEditing) {
                await adminUpdateClip(pass, clipEditing, payload);
                toast.success("Clip updated");
            } else {
                await adminCreateClip(pass, payload);
                toast.success("Clip added");
            }
            await reloadClips();
            startNewClip();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };
    const deleteClip = async (id) => {
        if (!window.confirm("Delete this clip permanently?")) return;
        try {
            await adminDeleteClip(pass, id);
            toast.success("Clip deleted");
            await reloadClips();
            if (clipEditing === id) startNewClip();
        } catch {
            toast.error("Delete failed");
        }
    };
    const approveClip = async (id) => {
        try {
            await adminApproveClip(pass, id);
            toast.success("Clip published");
            await reloadClips();
        } catch {
            toast.error("Approve failed");
        }
    };
    const archiveClip = async (id) => {
        try {
            await adminArchiveClip(pass, id);
            toast.success("Clip archived");
            await reloadClips();
        } catch {
            toast.error("Archive failed");
        }
    };
    const toggleFeatureClip = async (clip) => {
        try {
            await adminFeatureClip(pass, clip.id, !clip.featured);
            toast.success(
                !clip.featured ? "Marked as featured" : "Removed from featured",
            );
            await reloadClips();
        } catch {
            toast.error("Update failed");
        }
    };
    const switchStatusFilter = async (filter) => {
        setClipStatusFilter(filter);
        try {
            await reloadClips(filter);
        } catch {
            toast.error("Failed to load clips");
        }
    };
    const importClips = async (e) => {
        e.preventDefault();
        const urls = importUrls
            .split(/\s|\n|,/)
            .map((s) => s.trim())
            .filter(Boolean);
        if (urls.length === 0) {
            toast.error("Paste at least one URL");
            return;
        }
        setImporting(true);
        try {
            const res = await adminImportClips(pass, urls);
            toast.success(`${res.count} clip(s) queued for review`);
            setImportUrls("");
            // After import, jump to pending tab so the user can review.
            await switchStatusFilter("pending");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed");
        } finally {
            setImporting(false);
        }
    };
    const onPasteEmbed = (v) => {
        setClipForm((f) => ({
            ...f,
            embed_url: v,
            source: detectSource(v),
        }));
    };

    if (!authed) {
        return (
            <main
                data-testid="admin-login-page"
                className="min-h-screen flex items-center justify-center px-6"
            >
                <form
                    onSubmit={onLogin}
                    className="w-full max-w-md border border-border p-10"
                    data-testid="admin-login-form"
                >
                    <p className="text-label text-muted-foreground mb-4">Admin</p>
                    <h1 className="font-serif-display text-4xl tracking-tighter mb-8">
                        Studio Access
                    </h1>
                    <label className="block">
                        <span className="text-label text-muted-foreground">
                            Passcode
                        </span>
                        <input
                            type="password"
                            value={pass}
                            onChange={(e) => setPass(e.target.value)}
                            data-testid="admin-passcode-input"
                            className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3"
                            autoFocus
                        />
                    </label>
                    <button
                        type="submit"
                        data-testid="admin-login-submit"
                        className="mt-8 w-full bg-foreground text-background px-6 py-4 text-label hover:bg-foreground/90 transition-colors"
                    >
                        Enter
                    </button>
                </form>
            </main>
        );
    }

    return (
        <main
            data-testid="admin-page"
            className="pt-32 pb-32 mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14"
        >
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
                <div>
                    <p className="text-label text-muted-foreground mb-2">Admin</p>
                    <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tighter">
                        Studio Console
                    </h1>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        localStorage.removeItem("admin_pass");
                        setAuthed(false);
                        setPass("");
                    }}
                    data-testid="admin-logout"
                    className="text-label text-muted-foreground hover:text-foreground underline-anim"
                >
                    Sign out
                </button>
            </div>

            <div className="flex gap-8 border-b border-border mb-10">
                {["projects", "clips", "settings", "inquiries"].map((t) => (
                    <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        data-testid={`admin-tab-${t}`}
                        className={`text-label py-4 -mb-px border-b ${
                            tab === t
                                ? "text-foreground border-foreground"
                                : "text-muted-foreground border-transparent hover:text-foreground"
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {tab === "projects" && (
                <div className="grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-label text-muted-foreground">
                                Projects ({projects.length})
                            </p>
                            <button
                                type="button"
                                onClick={startNew}
                                data-testid="admin-new-project"
                                className="text-label underline-anim hover:text-foreground"
                            >
                                + New
                            </button>
                        </div>
                        <ul className="divide-y divide-border">
                            {projects.map((p) => (
                                <li
                                    key={p.id}
                                    className="py-4 flex items-start justify-between gap-4"
                                >
                                    <button
                                        type="button"
                                        onClick={() => startEdit(p)}
                                        data-testid={`admin-edit-${p.id}`}
                                        className="text-left flex-1 hover:text-foreground"
                                    >
                                        <p className="font-serif-display text-lg">
                                            {p.title}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {p.year} — {p.category}
                                        </p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deleteProject(p.id)}
                                        data-testid={`admin-delete-${p.id}`}
                                        className="text-xs text-muted-foreground hover:text-destructive"
                                    >
                                        Delete
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <form
                        onSubmit={saveProject}
                        data-testid="admin-project-form"
                        className="lg:col-span-7 border border-border p-8 space-y-6"
                    >
                        <p className="text-label text-muted-foreground">
                            {editing ? "Edit project" : "New project"}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-6">
                            <Field
                                label="Title"
                                value={form.title}
                                onChange={(v) =>
                                    setForm((f) => ({ ...f, title: v }))
                                }
                                testid="admin-field-title"
                                required
                            />
                            <Field
                                label="Year"
                                type="number"
                                value={form.year}
                                onChange={(v) =>
                                    setForm((f) => ({ ...f, year: v }))
                                }
                                testid="admin-field-year"
                                required
                            />
                            <Field
                                label="Role"
                                value={form.role}
                                onChange={(v) =>
                                    setForm((f) => ({ ...f, role: v }))
                                }
                                testid="admin-field-role"
                            />
                            <label className="block">
                                <span className="text-label text-muted-foreground">
                                    Category
                                </span>
                                <select
                                    value={form.category}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            category: e.target.value,
                                        }))
                                    }
                                    data-testid="admin-field-category"
                                    className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3"
                                >
                                    {CATEGORIES.map((c) => (
                                        <option
                                            key={c}
                                            value={c}
                                            className="bg-background"
                                        >
                                            {c}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <Field
                            label="Thumbnail URL"
                            value={form.thumbnail_url}
                            onChange={(v) =>
                                setForm((f) => ({ ...f, thumbnail_url: v }))
                            }
                            testid="admin-field-thumbnail"
                        />
                        <Field
                            label="Embed URL (Vimeo / YouTube)"
                            value={form.embed_url}
                            onChange={(v) =>
                                setForm((f) => ({ ...f, embed_url: v }))
                            }
                            testid="admin-field-embed"
                        />

                        <label className="block">
                            <span className="text-label text-muted-foreground">
                                Description
                            </span>
                            <textarea
                                value={form.description}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        description: e.target.value,
                                    }))
                                }
                                rows={3}
                                data-testid="admin-field-description"
                                className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 resize-none"
                            />
                        </label>

                        <label className="block">
                            <span className="text-label text-muted-foreground">
                                Gallery URLs (one per line)
                            </span>
                            <textarea
                                value={form.gallery_urls}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        gallery_urls: e.target.value,
                                    }))
                                }
                                rows={3}
                                data-testid="admin-field-gallery"
                                className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 resize-none"
                            />
                        </label>

                        <label className="block">
                            <span className="text-label text-muted-foreground">
                                Awards (one per line)
                            </span>
                            <textarea
                                value={form.awards}
                                onChange={(e) =>
                                    setForm((f) => ({
                                        ...f,
                                        awards: e.target.value,
                                    }))
                                }
                                rows={2}
                                data-testid="admin-field-awards"
                                className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 resize-none"
                            />
                        </label>

                        <div className="flex items-center gap-8">
                            <label className="flex items-center gap-3 text-label text-muted-foreground">
                                <input
                                    type="checkbox"
                                    checked={!!form.featured}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            featured: e.target.checked,
                                        }))
                                    }
                                    data-testid="admin-field-featured"
                                />
                                Featured on home
                            </label>
                            <label className="flex items-center gap-3 text-label text-muted-foreground">
                                Order
                                <input
                                    type="number"
                                    value={form.order}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            order: e.target.value,
                                        }))
                                    }
                                    data-testid="admin-field-order"
                                    className="w-20 bg-transparent border-b border-border focus:border-foreground outline-none py-1 text-foreground"
                                />
                            </label>
                        </div>

                        <div className="flex items-center gap-4 pt-4">
                            <button
                                type="submit"
                                data-testid="admin-project-save"
                                className="bg-foreground text-background px-6 py-3 text-label"
                            >
                                {editing ? "Save changes" : "Create project"}
                            </button>
                            {editing && (
                                <button
                                    type="button"
                                    onClick={startNew}
                                    className="text-label text-muted-foreground underline-anim"
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            )}

            {tab === "clips" && (
                <div
                    data-testid="admin-clips-tab"
                    className="space-y-10"
                >
                    {/* Status sub-filters */}
                    <div
                        data-testid="admin-clip-status-filters"
                        className="flex flex-wrap gap-6 border-b border-border pb-4"
                    >
                        {CLIP_STATUS_FILTERS.map((f) => (
                            <button
                                key={f.id}
                                type="button"
                                onClick={() => switchStatusFilter(f.id)}
                                data-testid={`admin-clip-filter-${f.id}`}
                                className={`text-label transition-colors ${
                                    clipStatusFilter === f.id
                                        ? "text-foreground"
                                        : "text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Social Import Queue */}
                    <form
                        onSubmit={importClips}
                        data-testid="admin-clip-import-form"
                        className="border border-border p-6 sm:p-8 space-y-4"
                    >
                        <div className="flex items-baseline justify-between gap-4">
                            <p className="text-label text-muted-foreground">
                                Social Import Queue
                            </p>
                            <p className="text-[11px] text-muted-foreground/80 italic">
                                Imported items enter Pending — never auto-publish
                            </p>
                        </div>
                        <textarea
                            rows={3}
                            value={importUrls}
                            onChange={(e) => setImportUrls(e.target.value)}
                            placeholder="Paste Threads, Instagram, Vimeo, or YouTube URLs — one per line"
                            data-testid="admin-clip-import-textarea"
                            className="w-full bg-transparent border border-border focus:border-foreground outline-none px-4 py-3 text-sm resize-none"
                        />
                        <div className="flex items-center gap-4">
                            <button
                                type="submit"
                                disabled={importing}
                                data-testid="admin-clip-import-submit"
                                className="bg-foreground text-background px-6 py-3 text-label disabled:opacity-50"
                            >
                                {importing ? "Queueing…" : "Queue for review"}
                            </button>
                            <button
                                type="button"
                                onClick={startNewClip}
                                data-testid="admin-new-clip"
                                className="text-label text-muted-foreground underline-anim hover:text-foreground"
                            >
                                + Add manually
                            </button>
                        </div>
                    </form>

                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <p className="text-label text-muted-foreground mb-6">
                                {clipStatusFilter === "all"
                                    ? "All clips"
                                    : CLIP_STATUS_FILTERS.find(
                                          (f) => f.id === clipStatusFilter,
                                      )?.label}{" "}
                                ({clips.length})
                            </p>

                            {clips.length === 0 ? (
                                <p
                                    data-testid={`admin-clip-empty-${clipStatusFilter}`}
                                    className="text-muted-foreground text-sm italic py-8 border-y border-border"
                                >
                                    {clipStatusFilter === "pending"
                                        ? "No new social clips awaiting review."
                                        : clipStatusFilter === "archived"
                                          ? "Older work lives here."
                                          : clipStatusFilter === "featured"
                                            ? "No featured clips yet — toggle ⭐ on a published clip."
                                            : "No clips in this view."}
                                </p>
                            ) : (
                                <ul className="divide-y divide-border">
                                    {clips.map((c) => (
                                        <li
                                            key={c.id}
                                            data-testid={`admin-clip-row-${c.id}`}
                                            className="py-5 flex items-start gap-4"
                                        >
                                            <div className="w-20 h-14 bg-muted shrink-0 overflow-hidden">
                                                {(c.poster_url ||
                                                    c.thumbnail_url) && (
                                                    <img
                                                        src={
                                                            c.poster_url ||
                                                            c.thumbnail_url
                                                        }
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        startEditClip(c)
                                                    }
                                                    data-testid={`admin-edit-clip-${c.id}`}
                                                    className="text-left block w-full hover:text-foreground"
                                                >
                                                    <p className="font-serif-display text-base leading-tight truncate">
                                                        {c.title ||
                                                            c.project_name ||
                                                            c.embed_url ||
                                                            "Untitled clip"}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {[
                                                            c.year,
                                                            c.category,
                                                            c.source,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" — ")}
                                                    </p>
                                                </button>

                                                <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] tracking-[0.2em] uppercase">
                                                    <span
                                                        data-testid={`admin-clip-status-${c.id}`}
                                                        className="text-foreground/70 border border-foreground/30 px-2 py-0.5"
                                                    >
                                                        {STATUS_BADGES[c.status] ||
                                                            "Live"}
                                                    </span>
                                                    {c.featured && (
                                                        <span className="text-foreground/70 border border-foreground/30 px-2 py-0.5">
                                                            Featured
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                                                    {c.status === "pending" && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                approveClip(c.id)
                                                            }
                                                            data-testid={`admin-approve-clip-${c.id}`}
                                                            className="hover:text-foreground underline-anim"
                                                        >
                                                            Approve
                                                        </button>
                                                    )}
                                                    {c.status === "archived" && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                approveClip(c.id)
                                                            }
                                                            data-testid={`admin-republish-clip-${c.id}`}
                                                            className="hover:text-foreground underline-anim"
                                                        >
                                                            Republish
                                                        </button>
                                                    )}
                                                    {c.status !== "archived" && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                archiveClip(c.id)
                                                            }
                                                            data-testid={`admin-archive-clip-${c.id}`}
                                                            className="hover:text-foreground underline-anim"
                                                        >
                                                            Archive
                                                        </button>
                                                    )}
                                                    {c.status === "published" && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                toggleFeatureClip(
                                                                    c,
                                                                )
                                                            }
                                                            data-testid={`admin-feature-clip-${c.id}`}
                                                            className="hover:text-foreground underline-anim"
                                                        >
                                                            {c.featured
                                                                ? "Unfeature"
                                                                : "Feature ⭐"}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            deleteClip(c.id)
                                                        }
                                                        data-testid={`admin-delete-clip-${c.id}`}
                                                        className="hover:text-destructive"
                                                    >
                                                        {c.status === "pending"
                                                            ? "Reject"
                                                            : "Delete"}
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <form
                            onSubmit={saveClip}
                            data-testid="admin-clip-form"
                            className="lg:col-span-7 border border-border p-8 space-y-6"
                        >
                            <p className="text-label text-muted-foreground">
                                {clipEditing ? "Edit clip" : "New clip"} — paste
                                any Threads, Instagram, Vimeo or YouTube link
                            </p>

                            <Field
                                label="Embed URL (Threads / Instagram / Vimeo / YouTube)"
                                value={clipForm.embed_url}
                                onChange={onPasteEmbed}
                                testid="admin-clip-embed"
                            />

                            <div className="grid sm:grid-cols-2 gap-6">
                                <Field
                                    label="Title"
                                    value={clipForm.title}
                                    onChange={(v) =>
                                        setClipForm((f) => ({ ...f, title: v }))
                                    }
                                    testid="admin-clip-title"
                                />
                                <Field
                                    label="Year"
                                    type="number"
                                    value={clipForm.year}
                                    onChange={(v) =>
                                        setClipForm((f) => ({ ...f, year: v }))
                                    }
                                    testid="admin-clip-year"
                                />
                            </div>

                            <div className="grid sm:grid-cols-2 gap-6">
                                <Field
                                    label="Role (optional)"
                                    value={clipForm.role}
                                    onChange={(v) =>
                                        setClipForm((f) => ({ ...f, role: v }))
                                    }
                                    testid="admin-clip-role"
                                />
                                <label className="block">
                                    <span className="text-label text-muted-foreground">
                                        Category
                                    </span>
                                    <select
                                        value={clipForm.category}
                                        onChange={(e) =>
                                            setClipForm((f) => ({
                                                ...f,
                                                category: e.target.value,
                                            }))
                                        }
                                        data-testid="admin-clip-category"
                                        className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3"
                                    >
                                        {CLIP_CATEGORIES.map((c) => (
                                            <option
                                                key={c}
                                                value={c}
                                                className="bg-background"
                                            >
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <Field
                                label="Project name (optional — associates clip with a project)"
                                value={clipForm.project_name}
                                onChange={(v) =>
                                    setClipForm((f) => ({
                                        ...f,
                                        project_name: v,
                                    }))
                                }
                                testid="admin-clip-project-name"
                            />

                            <div className="grid sm:grid-cols-2 gap-6">
                                <Field
                                    label="Thumbnail URL"
                                    value={clipForm.thumbnail_url}
                                    onChange={(v) =>
                                        setClipForm((f) => ({
                                            ...f,
                                            thumbnail_url: v,
                                        }))
                                    }
                                    testid="admin-clip-thumbnail"
                                />
                                <Field
                                    label="Custom poster URL (optional)"
                                    value={clipForm.poster_url}
                                    onChange={(v) =>
                                        setClipForm((f) => ({
                                            ...f,
                                            poster_url: v,
                                        }))
                                    }
                                    testid="admin-clip-poster"
                                />
                            </div>

                            <label className="block">
                                <span className="text-label text-muted-foreground">
                                    Caption (optional)
                                </span>
                                <textarea
                                    rows={2}
                                    value={clipForm.caption}
                                    onChange={(e) =>
                                        setClipForm((f) => ({
                                            ...f,
                                            caption: e.target.value,
                                        }))
                                    }
                                    data-testid="admin-clip-caption"
                                    className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 resize-none"
                                />
                            </label>

                            <div className="grid sm:grid-cols-3 gap-6">
                                <label className="block">
                                    <span className="text-label text-muted-foreground">
                                        Source
                                    </span>
                                    <select
                                        value={clipForm.source}
                                        onChange={(e) =>
                                            setClipForm((f) => ({
                                                ...f,
                                                source: e.target.value,
                                            }))
                                        }
                                        data-testid="admin-clip-source"
                                        className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3"
                                    >
                                        {[
                                            "upload",
                                            "threads",
                                            "instagram",
                                            "vimeo",
                                            "youtube",
                                        ].map((s) => (
                                            <option
                                                key={s}
                                                value={s}
                                                className="bg-background"
                                            >
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="block">
                                    <span className="text-label text-muted-foreground">
                                        Status
                                    </span>
                                    <select
                                        value={clipForm.status}
                                        onChange={(e) =>
                                            setClipForm((f) => ({
                                                ...f,
                                                status: e.target.value,
                                            }))
                                        }
                                        data-testid="admin-clip-status-select"
                                        className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3"
                                    >
                                        {["pending", "published", "archived"].map(
                                            (s) => (
                                                <option
                                                    key={s}
                                                    value={s}
                                                    className="bg-background"
                                                >
                                                    {s}
                                                </option>
                                            ),
                                        )}
                                    </select>
                                </label>
                                <Field
                                    label="Order"
                                    type="number"
                                    value={clipForm.order}
                                    onChange={(v) =>
                                        setClipForm((f) => ({ ...f, order: v }))
                                    }
                                    testid="admin-clip-order"
                                />
                            </div>

                            <div className="grid sm:grid-cols-[1fr_auto] gap-6 pt-2 items-end">
                                <div className="space-y-4">
                                    <label className="flex items-center gap-3 text-label text-muted-foreground">
                                        <input
                                            type="checkbox"
                                            checked={!!clipForm.featured}
                                            onChange={(e) =>
                                                setClipForm((f) => ({
                                                    ...f,
                                                    featured:
                                                        e.target.checked,
                                                }))
                                            }
                                            data-testid="admin-clip-featured"
                                        />
                                        Featured ⭐ — appears on Marquee & top of
                                        Recent Visuals
                                    </label>
                                    <label className="flex items-center gap-3 text-label text-muted-foreground">
                                        <input
                                            type="checkbox"
                                            checked={
                                                !!clipForm.show_intro_overlay
                                            }
                                            onChange={(e) =>
                                                setClipForm((f) => ({
                                                    ...f,
                                                    show_intro_overlay:
                                                        e.target.checked,
                                                }))
                                            }
                                            data-testid="admin-clip-intro-overlay"
                                        />
                                        Show intro overlay (title + caption fade
                                        in briefly on reel play)
                                    </label>
                                </div>
                                <label className="block">
                                    <span className="text-label text-muted-foreground">
                                        Auto-advance (sec, 3–60)
                                    </span>
                                    <input
                                        type="number"
                                        step={1}
                                        placeholder="default"
                                        value={
                                            clipForm.auto_advance_seconds ?? ""
                                        }
                                        onChange={(e) =>
                                            setClipForm((f) => ({
                                                ...f,
                                                auto_advance_seconds:
                                                    e.target.value,
                                            }))
                                        }
                                        data-testid="admin-clip-auto-advance-seconds"
                                        className="mt-3 w-32 bg-transparent border-b border-border focus:border-foreground outline-none py-2 text-foreground"
                                    />
                                </label>
                            </div>

                            <div className="flex items-center gap-4 pt-4">
                                <button
                                    type="submit"
                                    data-testid="admin-clip-save"
                                    className="bg-foreground text-background px-6 py-3 text-label"
                                >
                                    {clipEditing
                                        ? "Save changes"
                                        : "Add clip"}
                                </button>
                                {clipEditing && (
                                    <button
                                        type="button"
                                        onClick={startNewClip}
                                        className="text-label text-muted-foreground underline-anim"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {tab === "settings" && settings && (
                <form
                    onSubmit={saveSettings}
                    data-testid="admin-settings-form"
                    className="max-w-2xl border border-border p-8 space-y-6"
                >
                    <Field
                        label="Reel embed URL (Vimeo / YouTube)"
                        value={settings.reel_embed_url || ""}
                        onChange={(v) =>
                            setSettings((s) => ({ ...s, reel_embed_url: v }))
                        }
                        testid="admin-setting-reel"
                    />
                    <Field
                        label="Hero background image URL (Home — static still behind the looping video)"
                        value={settings.hero_bg_url || ""}
                        onChange={(v) =>
                            setSettings((s) => ({ ...s, hero_bg_url: v }))
                        }
                        testid="admin-setting-hero-bg"
                    />
                    <Field
                        label="About portrait image URL"
                        value={settings.portrait_url || ""}
                        onChange={(v) =>
                            setSettings((s) => ({ ...s, portrait_url: v }))
                        }
                        testid="admin-setting-portrait"
                    />
                    <Field
                        label="Contact email"
                        value={settings.contact_email || ""}
                        onChange={(v) =>
                            setSettings((s) => ({ ...s, contact_email: v }))
                        }
                        testid="admin-setting-email"
                    />
                    <Field
                        label="Instagram URL"
                        value={settings.instagram_url || ""}
                        onChange={(v) =>
                            setSettings((s) => ({ ...s, instagram_url: v }))
                        }
                        testid="admin-setting-instagram"
                    />
                    <Field
                        label="Threads URL"
                        value={settings.threads_url || ""}
                        onChange={(v) =>
                            setSettings((s) => ({ ...s, threads_url: v }))
                        }
                        testid="admin-setting-threads"
                    />
                    <Field
                        label="IMDb URL"
                        value={settings.imdb_url || ""}
                        onChange={(v) =>
                            setSettings((s) => ({ ...s, imdb_url: v }))
                        }
                        testid="admin-setting-imdb"
                    />
                    <label className="block">
                        <span className="text-label text-muted-foreground">
                            Show "Recently Updated" badge
                        </span>
                        <select
                            value={settings.badge_mode || "auto"}
                            onChange={(e) =>
                                setSettings((s) => ({
                                    ...s,
                                    badge_mode: e.target.value,
                                }))
                            }
                            data-testid="admin-setting-badge-mode"
                            className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3"
                        >
                            <option value="auto" className="bg-background">
                                Auto — only within 24h of a new clip
                            </option>
                            <option value="on" className="bg-background">
                                On — always show
                            </option>
                            <option value="off" className="bg-background">
                                Off — never show
                            </option>
                        </select>
                    </label>
                    <button
                        type="submit"
                        data-testid="admin-settings-save"
                        className="bg-foreground text-background px-6 py-3 text-label"
                    >
                        Save settings
                    </button>
                </form>
            )}

            {tab === "inquiries" && (
                <div data-testid="admin-inquiries-list">
                    {inquiries.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            No inquiries yet.
                        </p>
                    ) : (
                        <ul className="divide-y divide-border">
                            {inquiries.map((i) => (
                                <li key={i.id} className="py-6">
                                    <div className="flex items-baseline justify-between gap-4 mb-2">
                                        <p className="font-serif-display text-xl">
                                            {i.name}
                                            <span className="text-muted-foreground text-sm ml-3">
                                                &lt;{i.email}&gt;
                                            </span>
                                        </p>
                                        <span className="text-label text-muted-foreground">
                                            {new Date(i.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    {i.project_type && (
                                        <p className="text-label text-muted-foreground mb-2">
                                            {i.project_type}
                                        </p>
                                    )}
                                    <p className="text-foreground/85 leading-relaxed whitespace-pre-wrap">
                                        {i.message}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </main>
    );
};

const Field = ({ label, value, onChange, type = "text", testid, required }) => (
    <label className="block">
        <span className="text-label text-muted-foreground">{label}</span>
        <input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            required={required}
            className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3"
        />
    </label>
);

export default Admin;
